/**
 * Admin Dashboard Component
 * Manages user approvals and admin functions
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { apiGet, apiPost } from '../../utils/api';
import BankFeedPanel from './BankFeedPanel';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch pending users
   */
  const fetchPendingUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiGet('/api/auth/pending-users');
      setPendingUsers(response.users);
    } catch (error) {
      console.error('Error fetching pending users:', error);
      setError('Failed to load pending users');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Approve a user
   * @param {string} userId - User ID to approve
   */
  const handleApproveUser = async (userId) => {
    try {
      await apiPost(`/api/auth/approve-user/${userId}`);
      setPendingUsers(prev => prev.filter(user => user._id !== userId));
      alert('User approved successfully!');
    } catch (error) {
      console.error('Error approving user:', error);
      alert('Failed to approve user');
    }
  };

  /**
   * Reject a user
   * @param {string} userId - User ID to reject
   */
  const handleRejectUser = async (userId) => {
    if (!window.confirm('Are you sure you want to reject this user? This action cannot be undone.')) {
      return;
    }

    try {
      await apiPost(`/api/auth/reject-user/${userId}`);
      setPendingUsers(prev => prev.filter(user => user._id !== userId));
      alert('User rejected successfully!');
    } catch (error) {
      console.error('Error rejecting user:', error);
      alert('Failed to reject user');
    }
  };

  // Fetch pending users on component mount
  useEffect(() => {
    fetchPendingUsers();
  }, []);

  if (!user?.isAdmin) {
    return (
      <div className="admin-access-denied">
        <h2>Access Denied</h2>
        <p>You need admin privileges to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading pending users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-error">
        <h3>Error</h3>
        <p>{error}</p>
        <button onClick={fetchPendingUsers} className="retry-button">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="header-content">
          <div className="header-left">
            <h2>Admin Dashboard</h2>
            <p>Manage user approvals and system settings</p>
          </div>
          <div className="header-actions">
            <button 
              onClick={() => window.location.href = '/wealth'}
              className="back-button"
              title="Back to Main Dashboard"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"></path>
              </svg>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="admin-section">
        <div className="section-header">
          <h3>Pending User Approvals</h3>
          <span className="pending-count">{pendingUsers.length} pending</span>
        </div>

        {pendingUsers.length === 0 ? (
          <div className="no-pending-users">
            <p>No users pending approval</p>
          </div>
        ) : (
          <div className="pending-users-list">
            {pendingUsers.map(user => (
              <div key={user._id} className="pending-user-card">
                <div className="user-info">
                  <div className="user-name">
                    {user.firstName} {user.lastName}
                  </div>
                  <div className="user-email">{user.email}</div>
                  <div className="user-date">
                    Registered: {new Date(user.createdAt).toLocaleDateString()}
                  </div>
                </div>
                
                <div className="user-actions">
                  <button
                    onClick={() => handleApproveUser(user._id)}
                    className="approve-button"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleRejectUser(user._id)}
                    className="reject-button"
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BankFeedPanel />

      <div className="admin-section">
        <h3>System Information</h3>
        <div className="system-info">
          <div className="info-item">
            <span className="label">Admin User:</span>
            <span className="value">{user.firstName} {user.lastName}</span>
          </div>
          <div className="info-item">
            <span className="label">Email:</span>
            <span className="value">{user.email}</span>
          </div>
          <div className="info-item">
            <span className="label">Last Login:</span>
            <span className="value">
              {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
